import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Folder, FolderOpen, Inbox, Layers, Plus, Pencil, Trash2, Check, X } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });
const API = `${BACKEND}/api/admin/website-vision-folders`;

function FolderRow({ f, active, count, onSelect, onDrop, onRename, onDelete }) {
  const [over, setOver] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(f.name);
  const Icon = f.folder_id === 'all' ? Layers : f.folder_id === 'unfiled' ? Inbox : active ? FolderOpen : Folder;
  const custom = !['all', 'unfiled'].includes(f.folder_id);
  const save = () => { if (name.trim()) onRename(name.trim()); setEditing(false); };
  return (
    <div
      onDragOver={(e) => { if (f.folder_id !== 'all') { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData('text/vision-id'); if (id && f.folder_id !== 'all') onDrop(id); }}
      data-testid={`folder-${f.folder_id}`}
      className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer text-sm transition-colors ${over ? 'bg-[#FFF7ED] ring-2 ring-[#F97316]' : active ? 'bg-[#EFF6FF] text-[#1D4ED8] font-bold' : 'text-[#334155] hover:bg-slate-50'}`}
      onClick={() => !editing && onSelect()}>
      <Icon size={15} className="shrink-0" />
      {editing ? (
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          onClick={(e) => e.stopPropagation()} data-testid={`folder-rename-input-${f.folder_id}`} className="flex-1 min-w-0 px-1.5 py-0.5 rounded border border-[#1D4ED8] text-sm outline-none" />
      ) : <span className="flex-1 min-w-0 truncate">{f.name}</span>}
      {editing ? (
        <button type="button" onClick={(e) => { e.stopPropagation(); save(); }} className="text-emerald-600"><Check size={14} /></button>
      ) : (
        <>
          {custom && (
            <span className="hidden group-hover:flex items-center gap-1">
              <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`folder-rename-${f.folder_id}`} className="text-[#94A3B8] hover:text-[#1D4ED8]"><Pencil size={12} /></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`folder-delete-${f.folder_id}`} className="text-[#94A3B8] hover:text-red-500"><Trash2 size={12} /></button>
            </span>
          )}
          <span className="text-[11px] font-bold text-[#94A3B8] group-hover:hidden" data-testid={`folder-count-${f.folder_id}`}>{count}</span>
        </>
      )}
    </div>
  );
}

export default function FolderSidebar({ folders, setFolders, list, current, onSelect, onMove }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const count = (id) => (id === 'all' ? list.length : id === 'unfiled' ? list.filter((v) => !v.folder_id || !folders.some((f) => f.folder_id === v.folder_id)).length : list.filter((v) => v.folder_id === id).length);

  const add = async () => {
    if (!name.trim()) return;
    try { const r = await ax.post(API, { name: name.trim() }); setFolders([...folders, r.data]); setName(''); setAdding(false); toast.success('Folder created'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not create folder'); }
  };
  const rename = async (f, n) => {
    try { await ax.patch(`${API}/${f.folder_id}`, { name: n }); setFolders(folders.map((x) => (x.folder_id === f.folder_id ? { ...x, name: n } : x))); }
    catch { toast.error('Rename failed'); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"? Visions inside move to Unfiled.`)) return;
    try { await ax.delete(`${API}/${f.folder_id}`); setFolders(folders.filter((x) => x.folder_id !== f.folder_id)); if (current === f.folder_id) onSelect('all'); }
    catch { toast.error('Delete failed'); }
  };

  const rows = [{ folder_id: 'all', name: 'All visions' }, { folder_id: 'unfiled', name: 'Unfiled' }, ...folders];
  return (
    <aside className="bg-white border border-[#E2E8F0] rounded-xl p-2.5 h-fit lg:sticky lg:top-4" data-testid="folder-sidebar">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#94A3B8] px-2.5 pt-1 pb-2">Folders</div>
      <div className="space-y-0.5">
        {rows.map((f) => (
          <FolderRow key={f.folder_id} f={f} active={current === f.folder_id} count={count(f.folder_id)} onSelect={() => onSelect(f.folder_id)}
            onDrop={(vid) => onMove(vid, f.folder_id === 'unfiled' ? null : f.folder_id)} onRename={(n) => rename(f, n)} onDelete={() => remove(f)} />
        ))}
      </div>
      {adding ? (
        <div className="flex items-center gap-1 mt-2 px-1">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); if (e.key === 'Escape') setAdding(false); }}
            placeholder="Folder name" data-testid="folder-new-input" className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-sm outline-none focus:border-[#1D4ED8]" />
          <button type="button" onClick={add} data-testid="folder-new-save" className="p-1.5 rounded-lg bg-[#1D4ED8] text-white"><Check size={14} /></button>
          <button type="button" onClick={() => setAdding(false)} className="p-1.5 text-[#94A3B8]"><X size={14} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} data-testid="folder-new-btn" className="w-full mt-2 inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-semibold text-[#1D4ED8] hover:bg-[#EFF6FF]">
          <Plus size={14} /> New folder
        </button>
      )}
      <p className="text-[10px] text-[#94A3B8] px-2.5 mt-2">Tip: drag a vision onto a folder.</p>
    </aside>
  );
}
